import React, { useState } from "react";
import { SimpleMarkdown } from "../../../../../../util/markdownRenderer";
import "./GenericSuperAICard.scss";
import aiChatlogo from "../../../../../../assets/Images/aiChatlogo.png";
import { CheckpointConfirmCard } from "../CheckpointConfirmCard";
import { ThumbsUpIcon } from "../../../../../../assets/Icons/ThumbsUpIcon";
import { ThumbsDownIcon } from "../../../../../../assets/Icons/ThmbsDownIcon";
import { OrdersService } from "../../../../../../api/services/orders.service";

export const GenericSuperAICard = ({
  message,
  title,
  className = "",
  onSuggestedQuestionClick,
  // Checkpoint props
  checkpointPending = false,
  uiFields = [],
  onCheckpointConfirm,
  isCheckpointSubmitting = false,
  checkpointSubmitError = null,
  checkpointSubmitSuccess = false,
}) => {
  const d = new Date(message.timestamp || Date.now());
  const day = d.toLocaleDateString(undefined, {
    weekday: "long",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  const [feedback, setFeedback] = useState(null);

  const retrievedImages = message.retrieved_images || [];
  const references = message.references || [];
  const content = message.text || message.content || message.response || "";
  const isStreaming = Boolean(message.isStreaming);
  const suggestedQuestions = (message.suggested_questions || []).slice(0, 2);
  const hasCheckpointPending = checkpointPending && uiFields.length > 0;

  const handleFeedback = async (type) => {
    setFeedback(type);

    // Call feedback API if thread_id and turn_id are available
    const threadId = message.thread_id;
    const turnId = message.turn_id;

    // console.log('[Feedback] Attempting to submit feedback:', { type, threadId, turnId, message });

    if (threadId && turnId) {
      try {
        await OrdersService.updateResponseFeedback({
          threadId,
          turnId,
          feedback: type === "up",
        });
        //  console.log("[Feedback] Successfully submitted feedback");
      } catch (error) {
        console.error("[Feedback] Failed to submit feedback:", error);
      }
    } else {
      console.warn(
        "[Feedback] Missing threadId or turnId, cannot submit feedback",
      );
    }
  };

  return (
    <div className={`generic-super-ai-card ai-left ${className}`}>
      <div className="generic-super-ai-header">
        <img src={aiChatlogo} alt="Axon AI" className="generic-super-ai-logo" />
        <div className="generic-super-ai-title">
          <span>{title || "Axon AI"}</span>
          <div className="generic-super-ai-time">{`${day} ${time}`}</div>
        </div>
      </div>
      <div className="generic-super-ai-bubble">
        <div className="generic-super-ai-content markdown-content">
          <SimpleMarkdown>{content}</SimpleMarkdown>
          {isStreaming && <span className="streaming-cursor">▌</span>}
        </div>
        {retrievedImages.length > 0 && (
          <div className="generic-super-ai-images">
            {retrievedImages.map((img, index) => (
              <div key={index} className="generic-super-ai-image-wrapper">
                <img
                  src={img.image_path}
                  alt={`img`}
                  className="generic-super-ai-image"
                />
              </div>
            ))}
          </div>
        )}
        {!isStreaming && references.length > 0 && (
          <div className="generic-super-ai-sources">
            <span className="sources-label">Source</span>
            <ul className="sources-list">
              {references.map((ref, index) => (
                <li key={index} className="source-item">
                  <a
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="source-link"
                  >
                    {ref.document}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {hasCheckpointPending && (
          <CheckpointConfirmCard
            uiFields={uiFields}
            onConfirm={onCheckpointConfirm}
            isSubmitting={isCheckpointSubmitting}
            submitError={checkpointSubmitError}
            submitSuccess={checkpointSubmitSuccess}
          />
        )}
        {suggestedQuestions.length > 0 && !hasCheckpointPending && (
          <div className="generic-super-ai-suggested-questions">
            <span className="suggested-questions-label">Quick Suggestions</span>
            <div className="suggested-questions-list">
              {suggestedQuestions.map((question, index) => (
                <button
                  key={index}
                  className="suggested-question-item"
                  onClick={() => onSuggestedQuestionClick?.(question)}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="generic-super-ai-feedback">
        <button
          className={`feedback-btn ${feedback === "up" ? "active" : ""}`}
          onClick={() => handleFeedback("up")}
          aria-label="Thumbs up"
        >
          <ThumbsUpIcon
            width={20}
            height={20}
            color={feedback === "up" ? "#1A1A24" : "#A4A3B1"}
          />
        </button>
        <button
          className={`feedback-btn ${feedback === "down" ? "active" : ""}`}
          onClick={() => handleFeedback("down")}
          aria-label="Thumbs down"
        >
          <ThumbsDownIcon
            width={20}
            height={20}
            color={feedback === "down" ? "#1A1A24" : "#A4A3B1"}
          />
        </button>
      </div>
    </div>
  );
};
